/**
 * @file meExtras.ts
 * @brief The parts of `GET /api/auth/me` the auth context does not keep:
 *        `viewer` (recording transparency) and `dataControls` (company rules).
 *
 * @details
 * - `viewer` (sharing v2, DES 21-10-2026): whether the recording setup shows
 *   "Your company admins can see this meeting…" (employee-boardroom/
 *   recordingViewer.ts).
 * - `dataControls` (ADR-051, DES 21-10-2026): the user's OWN company's rules
 *   (null for personal accounts and when the server's switch is off):
 *   `canShareOutside`, `canExport`, and the lines to show (`shareLine`,
 *   `exportLine`). The UI only explains; the server refuses anyway.
 *
 * - `isOwner` (B31, ADR-059, 25-10-2026): the one Owner account (DES). Only
 *   the Owner sees Super Admin role changes and resets on other Super Admins.
 *   The server refuses them anyway (OWNER_ONLY).
 *
 * One cached /me read (5 min), refreshed when an admin saves the company's
 * data controls. No vendor cost.
 *
 * @references
 * - BACKEND auth/routes.js GET /me
 * - scratch/claude-code-report.md "Company data controls: Part 1" section 2
 *
 * @author DES
 * @date 21-10-2026
 */

import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { apiGet } from "@/shared/api/client";
import { authEndpoints } from "@/shared/api/endpoints";
import { useAuth } from "./auth-context";
import { dataControlsView, meetingExportView } from "./dataControlsView";

const ViewerFlagsSchema = z
  .object({
    isCompanyMember: z.boolean().optional().catch(false),
    isCompanyAdmin: z.boolean().optional().catch(false),
    companyAdminsCanSee: z.boolean().optional().catch(false),
    companyAdminsCanSeeNotes: z.boolean().optional().catch(false),
  })
  .passthrough();

const DataControlsSchema = z
  .object({
    shareInsideOnly: z.boolean().optional(),
    allowExport: z.boolean().optional(),
    canShareOutside: z.boolean().optional(),
    canExport: z.boolean().optional(),
    shareLine: z.string().nullable().optional(),
    exportLine: z.string().nullable().optional(),
  })
  .passthrough();

export type RecordingViewerFlags = z.infer<typeof ViewerFlagsSchema>;
export type MyDataControls = z.infer<typeof DataControlsSchema>;
export type MeExtras = {
  viewer: RecordingViewerFlags | null;
  dataControls: MyDataControls | null;
  isOwner: boolean;
};

export const meExtrasKey = ["auth", "me-extras"] as const;

export function useMeExtras() {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...meExtrasKey, user?.id ?? null],
    enabled: Boolean(user?.id),
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<MeExtras> => {
      const raw = await apiGet<{ viewer?: unknown; dataControls?: unknown; isOwner?: unknown }>(authEndpoints.me);
      const viewer = ViewerFlagsSchema.safeParse(raw?.viewer);
      const dc = DataControlsSchema.safeParse(raw?.dataControls);
      return {
        viewer: viewer.success ? viewer.data : null,
        dataControls: raw?.dataControls && dc.success ? dc.data : null,
        isOwner: raw?.isOwner === true,
      };
    },
  });
}

/** True only for the Owner account (B31); false while loading. */
export function useIsOwner(): boolean {
  return useMeExtras().data?.isOwner === true;
}

/** What the signed-in user may do under their own company's rules. Unknown
 *  (loading, personal, older server) reads as allowed: the server decides. */
export function useDataControls() {
  const q = useMeExtras();
  return dataControlsView(q.data?.dataControls ?? null);
}

/** Exports for one meeting: blocked by the viewer's company, or by the
 *  meeting owner's company (`exports_disabled` on the payload). */
export function useMeetingExport(meeting: object | null | undefined) {
  const q = useMeExtras();
  return meetingExportView(q.data?.dataControls ?? null, meeting);
}
